import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import API_BASE_URL from "../../utils/api";
import ScoringRubric from "./ScoringRubric";

const EXCLUDED_LEADERBOARD_USER_IDS = [];

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0 sec";

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins === 0) return `${secs} sec`;
  return `${mins} min ${secs} sec`;
};

const getPageTypeLabel = (pageContentType) => {
  if (pageContentType === "handwritten") return "Handwritten";
  if (pageContentType === "printed") return "Printed";
  return "Unknown";
};

const getActionLabel = (type) => {
  if (type === "transcribed") return "Transcribed";
  if (type === "reviewed") return "Reviewed";
  if (type === "saved_progress") return "Saved Progress";
  return "Activity";
};

const getContributionLabel = (item) => {
  const action = getActionLabel(item.type);
  const pageType = getPageTypeLabel(item.pageContentType);
  const documentTitle = item.documentTitle || "Untitled Document";

  return `${action} • ${documentTitle} • Page ${item.pageNumber} • ${pageType}`;
};

const getRankStyles = (rank) => {
  if (rank === 1) {
    return {
      backgroundColor: "#fff8dc",
      borderColor: "#e0b100",
      chipColor: "#b8860b",
      iconColor: "#d4a017",
      label: "1st Place",
    };
  }

  if (rank === 2) {
    return {
      backgroundColor: "#f5f5f5",
      borderColor: "#b0b0b0",
      chipColor: "#6e6e6e",
      iconColor: "#9e9e9e",
      label: "2nd Place",
    };
  }

  if (rank === 3) {
    return {
      backgroundColor: "#fff3e6",
      borderColor: "#c98a4a",
      chipColor: "#a65e2e",
      iconColor: "#b87333",
      label: "3rd Place",
    };
  }

  return {
    backgroundColor: "#ffffff",
    borderColor: "#eeeeee",
    chipColor: "#1976d2",
    iconColor: "#1976d2",
    label: null,
  };
};

const Leaderboard = ({ eventId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState({});

  const fetchLeaderboard = async () => {
    try {
      const [leaderboardRes, participantsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/transcribeathon/${eventId}/leaderboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        axios.get(
          `${API_BASE_URL}/transcribeathon/public/events/${eventId}/participants`
        ),
      ]);

      const leaderboardRows = (leaderboardRes.data || []).filter(
        (row) => !EXCLUDED_LEADERBOARD_USER_IDS.includes(String(row.userId))
      );

      const participants = participantsRes.data || [];

      const leaderboardMap = new Map(
        leaderboardRows.map((row) => [String(row.userId), row])
      );

      const allRows = participants
        .map((participant) => {
          const userId = participant?.userId?._id || participant?.userId || "";
          const fullName =
            participant?.userId?.name ||
            participant.displayNameSnapshot ||
            "Participant";

          const existing = leaderboardMap.get(String(userId));

          if (existing) return existing;

          return {
            rank: null,
            userId: String(userId),
            name: fullName,
            transcribed: 0,
            reviewed: 0,
            printedTranscribed: 0,
            handwrittenTranscribed: 0,
            unknownTranscribed: 0,
            printedReviewed: 0,
            handwrittenReviewed: 0,
            unknownReviewed: 0,
            score: 0,
            totalTimeSeconds: 0,
            recentContributions: [],
          };
        })
        .filter(
          (row) => !EXCLUDED_LEADERBOARD_USER_IDS.includes(String(row.userId))
        );

      // Step 2: split active vs inactive
      const active = allRows.filter(
        (r) => (r.transcribed || 0) > 0 || (r.reviewed || 0) > 0
      );

      const inactive = allRows.filter(
        (r) => (r.transcribed || 0) === 0 && (r.reviewed || 0) === 0
      );

      // Step 3: sort ONLY active users
      active.sort((a, b) => {
        if ((b.score || 0) !== (a.score || 0))
          return (b.score || 0) - (a.score || 0);

        if ((b.transcribed || 0) !== (a.transcribed || 0))
          return (b.transcribed || 0) - (a.transcribed || 0);

        if ((b.reviewed || 0) !== (a.reviewed || 0))
          return (b.reviewed || 0) - (a.reviewed || 0);

        if ((a.totalTimeSeconds || 0) !== (b.totalTimeSeconds || 0))
          return (a.totalTimeSeconds || 0) - (b.totalTimeSeconds || 0);

        return String(a.name || "").localeCompare(String(b.name || ""));
      });

      // Step 4: assign ranks ONLY to active
      const rankedActive = active.map((row, index) => ({
        ...row,
        rank: index + 1,
      }));

      // Step 5: final order = ranked users first, then inactive
      const finalRows = [...rankedActive, ...inactive];

      setRows(finalRows);
    } catch (err) {
      console.error("Failed to fetch leaderboard", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRecentActivity = (userId) => {
    setExpandedActivity((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  useEffect(() => {
    if (!eventId) return;
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 15000);
    return () => clearInterval(interval);
  }, [eventId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
      <ScoringRubric />

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Event Leaderboard
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2">No participants yet.</Typography>
      ) : (
        rows.map((row, idx) => {
          const rankStyles = getRankStyles(row.rank);
          const hasNoActivity =
            (row.transcribed || 0) === 0 && (row.reviewed || 0) === 0;

          return (
            <Box
              key={String(row.userId)}
              sx={{
                border: "2px solid",
                borderColor: rankStyles.borderColor,
                borderRadius: 2,
                p: 1.5,
                mb: idx === rows.length - 1 ? 0 : 1.5,
                backgroundColor: hasNoActivity
                  ? "#fafafa"
                  : rankStyles.backgroundColor,
                opacity: hasNoActivity ? 0.9 : 1,
                boxShadow:
                  row.rank <= 3 ? "0 3px 10px rgba(0,0,0,0.05)" : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {row.rank <= 3 && (
                      <EmojiEventsIcon
                        sx={{ color: rankStyles.iconColor, fontSize: 20 }}
                      />
                    )}

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        wordBreak: "break-word",
                      }}
                    >
                      #{row.rank} {row.name}
                    </Typography>

                    {rankStyles.label && (
                      <Chip
                        label={rankStyles.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          backgroundColor: "#fff",
                          color: rankStyles.chipColor,
                          border: "1px solid",
                          borderColor: rankStyles.borderColor,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Chip
                  label={`Score ${row.score}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#fff",
                    color: rankStyles.chipColor,
                    border: "1px solid",
                    borderColor: rankStyles.borderColor,
                    flexShrink: 0,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mb: 1,
                }}
              >
                {hasNoActivity && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    No contributions yet
                  </Typography>
                )}
                <Chip label={`Saved ${row.savedProgress || 0}`} size="small" />
                <Chip label={`Transcribed ${row.transcribed}`} size="small" />
                <Chip label={`Reviewed ${row.reviewed}`} size="small" />
                <Chip
                  label={`Total time ${formatDuration(row.totalTimeSeconds)}`}
                  size="small"
                  color="secondary"
                />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Handwritten pages → Transcribed{" "}
                {row.handwrittenTranscribed ?? 0} | Reviewed{" "}
                {row.handwrittenReviewed ?? 0}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Printed pages → Transcribed {row.printedTranscribed ?? 0} |
                Reviewed {row.printedReviewed ?? 0}
              </Typography>

              {(row.unknownTranscribed > 0 || row.unknownReviewed > 0) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Unknown pages → Transcribed {row.unknownTranscribed ?? 0} |
                  Reviewed {row.unknownReviewed ?? 0}
                </Typography>
              )}

              {row.recentContributions?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />

                  <Button
                    onClick={() => toggleRecentActivity(String(row.userId))}
                    endIcon={
                      expandedActivity[String(row.userId)] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )
                    }
                    sx={{
                      p: 0,
                      minWidth: "unset",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#111111",
                      justifyContent: "flex-start",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Recent activity
                  </Button>

                  {expandedActivity[String(row.userId)] && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {row.recentContributions.map((item, index) => (
                        <Box
                          key={`${row.userId}-${item.pageNumber}-${index}`}
                          sx={{
                            p: 0.75,
                            borderRadius: 1,
                            backgroundColor: "#f8f9fb",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, display: "block" }}
                          >
                            {getContributionLabel(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Time: {formatDuration(item.durationSeconds || 0)} •
                            Points: {item.awardedPoints ?? 0}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })
      )}
    </Paper>
  );
};

export default Leaderboard;
